import React, { useRef, useState, useEffect, useMemo } from 'react';
import { LoadingSpinner } from 'components/common';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { FaPlane, FaCar, FaCheckCircle } from 'react-icons/fa';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import {
  MdPeople, MdWork, MdSyncAlt, MdSend, MdCalendarToday, MdGavel,
} from 'react-icons/md';
import { fetchCalendar } from 'redux/booking/calendarSlice';
import { fetchCalendarEvents } from 'redux/my/calendarSlice';
import CalendarEventDetailModal from 'components/app/crm/shared/CalendarEventDetailModal';
import { getEventIcon, getProjectRef } from 'components/app/crm/shared/calendarEventConfig';

const getIcon = (type) => getEventIcon(type, MdCalendarToday);

// ── Legend chips (compact — one chip per stage/group) ─────────────────────────
const LEGEND = [
  { Icon: FaPlane,   color: '#3b82f6', label: 'Travel',       textColor: 'tw-text-blue-600',    bg: 'tw-bg-blue-50' },
  { Icon: FaCar,     color: '#10b981', label: 'Car',          textColor: 'tw-text-emerald-600', bg: 'tw-bg-emerald-50' },
  { Icon: MdPeople,  color: '#3b82f6', label: 'Activities',   textColor: 'tw-text-blue-600',    bg: 'tw-bg-blue-50' },
  { Icon: MdSend,    color: '#f59e0b', label: 'Quotation',    textColor: 'tw-text-amber-600',   bg: 'tw-bg-amber-50' },
  { Icon: MdWork,    color: '#ef4444', label: 'Enter Job',    textColor: 'tw-text-red-600',     bg: 'tw-bg-red-50' },
  { Icon: MdSyncAlt, color: '#ec4899', label: 'Change Order', textColor: 'tw-text-pink-600',    bg: 'tw-bg-pink-50' },
  { Icon: MdGavel,   color: '#7c3aed', label: 'Waiver',       textColor: 'tw-text-violet-700',  bg: 'tw-bg-violet-50' },
];

const MyCalendarWidget = () => {
  const dispatch  = useDispatch();
  const calRef    = useRef(null);

  const { events: bookingEvents = [], loading: bookingLoading } = useSelector(s => s.booking_calendar);
  const { events: myEvents     = [], loading: myLoading }       = useSelector(s => s.my_calendar);

  const [title,       setTitle]       = useState('');
  const [dateRange,   setDateRange]   = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);

  // Fetch both sources when date range changes
  useEffect(() => {
    if (!dateRange) return;
    dispatch(fetchCalendar({ from_date: dateRange.start, end_date: dateRange.end }));
    dispatch(fetchCalendarEvents({ start: dateRange.start, end: dateRange.end }));
  }, [dispatch, dateRange]);

  const handleDatesSet = (info) => {
    setTitle(info.view.title);
    setDateRange({
      start: info.startStr.slice(0, 10),
      end:   info.endStr.slice(0, 10),
    });
  };

  const handleEventClick = (info) => {
    info.jsEvent.preventDefault();
    const ep   = info.event.extendedProps || {};
    const type = ep.type;

    if (type === 'travel' || type === 'car') {
      setDetailEvent({
        title:         info.event.title,
        start:         info.event.start,
        end:           info.event.end,
        color:         info.event.backgroundColor,
        type,
        note:          ep.detail,
        bookingNumber: ep.booking_number,
        bookingId:     ep.booking_id,
        statusName:    ep.status_name,
        editable:      false,
      });
      return;
    }

    setDetailEvent({
      title:       info.event.title,
      start:       info.event.start,
      end:         info.event.end,
      color:       info.event.backgroundColor,
      type,
      source_type: ep.source_type,
      status:      ep.status,
      note:        ep.note,
      assignee:    ep.assignee,
      contact:     ep.contact,
      resourceUrl: ep.resourceUrl,
      pcvNumber:   ep.pcv_number,
      qNumber:     ep.q_number,
      jNumber:     ep.j_number,
      editable:    false,
    });
  };

  const nav = (action) => calRef.current?.getApi()[action]();

  // Merge and style all events
  const allEvents = useMemo(() => {
    // Booking events
    const styled = bookingEvents.map(ev => ({
      ...ev,
      extendedProps: { ...ev.extendedProps, type: ev.extendedProps?.type || 'car' },
      backgroundColor: ev.extendedProps?.type === 'travel' ? '#3b82f6' : '#10b981',
      borderColor:     ev.extendedProps?.type === 'travel' ? '#2563eb' : '#059669',
      textColor: '#fff',
    }));

    // My calendar events
    const mine = myEvents.map(ev => ({
      id:    ev.id,
      title: ev.title,
      start: ev.start,
      end:   ev.end,
      allDay: ev.allDay,
      backgroundColor: ev.color || '#64748b',
      borderColor:     ev.color || '#64748b',
      textColor: '#fff',
      extendedProps: {
        source_type: ev.source_type,
        type:        ev.type,
        status:      ev.status,
        resourceUrl: ev.resourceUrl,
        note:        ev.note,
        assignee:    ev.assignee,
        contact:     ev.contact,
        pcv_number:  ev.pcv_number,
        q_number:    ev.q_number,
        j_number:    ev.j_number,
      },
    }));

    return [...styled, ...mine];
  }, [bookingEvents, myEvents]);

  const loading = bookingLoading || myLoading;

  return (
    <div className="tw-bg-white tw-rounded-2xl">

      {/* ── Header ── */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3.5 tw-border-b tw-border-slate-100">
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          <h3 className="tw-text-[14px] tw-font-bold tw-text-slate-900 tw-m-0 tw-mr-1">My Calendar</h3>
          {LEGEND.map(l => (
            <span key={l.label} className={`tw-inline-flex tw-items-center tw-gap-1 tw-text-[10px] tw-font-semibold ${l.textColor} ${l.bg} tw-px-1.5 tw-py-0.5 tw-rounded-xl`}>
              <l.Icon size={8} /> {l.label}
            </span>
          ))}
        </div>

        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-text-xs tw-font-bold tw-text-slate-700 tw-min-w-[100px] tw-text-center">{title}</span>
          <div className="tw-flex tw-border tw-border-slate-200 tw-rounded tw-overflow-hidden">
            <button onClick={() => nav('prev')}
              className="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-text-slate-500 hover:tw-bg-slate-50 tw-bg-white tw-cursor-pointer tw-border-0 tw-outline-none">
              <FiChevronLeft size={13} />
            </button>
            <button onClick={() => nav('next')}
              className="tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-text-slate-500 hover:tw-bg-slate-50 tw-bg-white tw-cursor-pointer tw-border-0 tw-outline-none tw-border-l tw-border-slate-200">
              <FiChevronRight size={13} />
            </button>
          </div>
          <button onClick={() => nav('today')}
            className="tw-px-2.5 tw-py-1 tw-text-[11px] tw-font-semibold tw-text-blue-600 tw-bg-blue-50 tw-border tw-border-blue-100 tw-rounded hover:tw-bg-blue-100 tw-cursor-pointer tw-outline-none tw-transition-colors">
            Today
          </button>
        </div>
      </div>

      {/* ── Calendar grid ── */}
      <div className="tw-px-3 tw-pt-2 tw-pb-3 tw-relative">
        {loading && (
          <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/60 tw-z-10 tw-rounded-b-2xl">
            <LoadingSpinner size="sm" />
          </div>
        )}
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={false}
          dayMaxEvents={3}
          height="auto"
          events={allEvents}
          datesSet={handleDatesSet}
          eventClick={handleEventClick}
          eventDisplay="block"
          eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          moreLinkClassNames="tw-text-slate-500 tw-font-semibold tw-text-xs"
          eventContent={(arg) => {
            const ep     = arg.event.extendedProps || {};
            const type   = ep.type || '';
            const isDone = ep.status === 'done';
            const Icon   = isDone ? FaCheckCircle : getIcon(type);
            const isOverdue = !isDone && arg.event.start && dayjs(arg.event.start).isBefore(dayjs());
            // Lead project events with their identifier, priority job → quote → PCV.
            const projectRef = getProjectRef(ep);
            return (
              <div className="tw-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-0.5 tw-overflow-hidden tw-w-full">
                <Icon size={9} className="tw-text-white/90 tw-shrink-0" />
                <span className={`tw-text-[10px] tw-font-semibold tw-text-white tw-truncate tw-flex-1 ${isDone ? 'tw-opacity-60 tw-line-through' : ''}`}>
                  {projectRef ? `${projectRef} · ${arg.event.title}` : arg.event.title}
                </span>
                {isOverdue && <span className="tw-text-white/80 tw-text-[9px] tw-shrink-0">!</span>}
              </div>
            );
          }}
        />
      </div>

      <CalendarEventDetailModal
        event={detailEvent}
        onClose={() => setDetailEvent(null)}
      />
    </div>
  );
};

export default MyCalendarWidget;
